import React, { useState, useEffect } from 'react';
import { Bell, Check, AlertTriangle, FileText, Database, Sparkles, Euro } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { ScrollArea } from './ui/scroll-area';
import { notificationAPI } from '../lib/api';
import { useNavigate } from 'react-router-dom';
import { cn } from '../lib/utils';

const CLEANING_TYPES = ['cleaning_photos', 'cleaning_cancelled', 'cleaning_request'];
const BILLING_TYPES = ['payment_reminder', 'factuur_betaald'];

const CATEGORIES = {
  reinigingen: {
    title: 'Meldingen reinigingen',
    icon: Sparkles,
    iconClass: 'text-purple-400',
    types: CLEANING_TYPES,
  },
  facturatie: {
    title: 'Meldingen facturatie',
    icon: Euro,
    iconClass: 'text-green-400',
    types: BILLING_TYPES,
  },
  overige: {
    title: 'Overige meldingen',
    icon: Bell,
    iconClass: 'text-amber-400',
    excludeTypes: [...CLEANING_TYPES, ...BILLING_TYPES, 'duplicate_customer'],
  },
};

const typeIcon = (type) => {
  switch (type) {
    case 'new_aanvraag': return <FileText className="w-4 h-4 text-green-500" />;
    case 'data_error': return <AlertTriangle className="w-4 h-4 text-yellow-500" />;
    case 'backup_complete': return <Database className="w-4 h-4 text-blue-500" />;
    case 'backup_failed': return <Database className="w-4 h-4 text-red-500" />;
    case 'cleaning_photos':
    case 'cleaning_cancelled':
    case 'cleaning_request': return <Sparkles className="w-4 h-4 text-purple-500" />;
    case 'payment_reminder':
    case 'factuur_betaald': return <Euro className="w-4 h-4 text-green-500" />;
    default: return <Bell className="w-4 h-4 text-muted-foreground" />;
  }
};

const timeAgo = (iso) => {
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const min = Math.floor(diff / 60000);
    if (min < 60) return `${Math.max(min, 0)} min geleden`;
    const uur = Math.floor(min / 60);
    if (uur < 24) return `${uur} uur geleden`;
    return new Date(iso).toLocaleDateString('nl-BE', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return ''; }
};

export const NotificationsPanel = ({ category = 'overige' }) => {
  const config = CATEGORIES[category] || CATEGORIES.overige;
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchAll = async () => {
    try {
      const params = config.types
        ? { types: config.types.join(',') }
        : { exclude_types: config.excludeTypes.join(',') };
      const res = await notificationAPI.getAll(false, 200, params);
      setNotifications(res.data || []);
    } catch (e) { /* stil */ }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const unread = notifications.filter(n => !n.read).length;

  const handleClick = async (n) => {
    if (!n.read) {
      try {
        await notificationAPI.markRead(n.id);
        setNotifications(prev => prev.map(x => x.id === n.id ? { ...x, read: true } : x));
      } catch (e) { /* stil */ }
    }
    if (n.type === 'new_aanvraag' && n.data?.aanvraag_id) navigate(`/aanvragen?highlight=${n.data.aanvraag_id}`);
    else if (n.type === 'cleaning_photos' && n.data?.property_id) navigate(`/properties/${n.data.property_id}?tab=documents`);
    else if (n.type === 'cleaning_cancelled') navigate(n.data?.property_id ? `/properties/${n.data.property_id}` : '/cleaning');
    else if (n.type === 'cleaning_request') navigate('/aanvragen');
    else if (n.type === 'payment_reminder' || n.type === 'factuur_betaald') navigate('/facturen');
    else if (n.type === 'backup_failed') navigate('/settings');
    else if (n.type === 'data_error') navigate(n.data?.error_type === 'missing_gps' ? '/adressen' : '/klanten');
  };

  const handleAllRead = async () => {
    try {
      const params = config.types
        ? { types: config.types.join(',') }
        : { exclude_types: config.excludeTypes.join(',') };
      await notificationAPI.markAllRead(params);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (e) { /* stil */ }
  };

  const HeaderIcon = config.icon;

  return (
    <Card className="border-border/50" data-testid={`notifications-panel-${category}`}>
      <CardHeader className="pb-3 flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <HeaderIcon className={cn('w-5 h-5', config.iconClass)} />
          {config.title}
          {unread > 0 && <Badge className="bg-red-500 text-white">{unread}</Badge>}
        </CardTitle>
        {unread > 0 && (
          <Button variant="ghost" size="sm" onClick={handleAllRead} data-testid={`mark-all-read-btn-${category}`}>
            <Check className="w-4 h-4 mr-1" />
            Alles gelezen
          </Button>
        )}
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <p className="text-sm text-muted-foreground px-6 pb-6">Laden...</p>
        ) : notifications.length === 0 ? (
          <p className="text-sm text-muted-foreground px-6 pb-6">Geen meldingen</p>
        ) : (
          <ScrollArea className="h-[320px]">
            <div className="divide-y divide-border/50">
              {notifications.map(n => (
                <button
                  key={n.id}
                  onClick={() => handleClick(n)}
                  className={cn(
                    'w-full text-left px-4 py-3 hover:bg-muted/50 transition-colors flex items-start gap-3',
                    !n.read && 'bg-amber-500/5'
                  )}
                  data-testid={`notification-item-${n.id}`}
                >
                  <span className="mt-0.5 shrink-0">{typeIcon(n.type)}</span>
                  <span className="min-w-0 flex-1">
                    <span className={cn('block text-sm truncate', !n.read ? 'font-semibold' : 'font-medium')}>{n.title}</span>
                    <span className="block text-xs text-muted-foreground line-clamp-2">{n.message}</span>
                    <span className="block text-[11px] text-muted-foreground/70 mt-0.5">{timeAgo(n.created_at)}</span>
                  </span>
                  {!n.read && <span className="mt-1.5 w-2 h-2 rounded-full bg-orange-500 shrink-0" />}
                </button>
              ))}
            </div>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
};

export default NotificationsPanel;
